import { api } from "./api";
import type { ProviderUsageInfo, UsageWindowInfo } from "./types";

/**
 * Helpers de exibição do uso do plano (conta ChatGPT) e cache de consulta.
 * O consumo é mostrado como "o que falta usar", não como o que já foi gasto.
 */

/** "3h 12m", "6d 4h", "25 min"… a partir de segundos restantes. */
export function formatCountdown(seconds: number): string {
  if (seconds <= 0) return "agora";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${Math.max(1, m)} min`;
}

/** Tempo até o reinício da janela de uso, quando a API informa. */
export function resetIn(w: UsageWindowInfo): string | null {
  const seconds =
    w.resetAfterSeconds !== undefined
      ? w.resetAfterSeconds
      : w.resetAt !== undefined
        ? w.resetAt - Math.floor(Date.now() / 1000)
        : null;
  return seconds === null ? null : formatCountdown(seconds);
}

/** Percentual ainda disponível na janela (o que falta usar). */
export function remainingPercent(w: UsageWindowInfo): number {
  return Math.max(0, Math.min(100, Math.round(100 - w.usedPercent)));
}

/** Rótulo curto da janela para o medidor junto à caixa de texto. */
export function shortWindowLabel(w: UsageWindowInfo): string {
  const l = w.label.toLowerCase();
  if (l.includes("reserve")) return "reserve";
  if (l.includes("5 hora") || l.includes("5h")) return "5h";
  if (l.includes("seman")) return "semanal";
  return w.label.length > 16 ? `${w.label.slice(0, 15)}…` : w.label;
}

/** Modelos servidos pela reserva (família Luna). */
export function isReserveModel(modelID: string): boolean {
  return modelID.toLowerCase().includes("luna");
}

/** Ordem de "avanço" do modelo: geração maior primeiro; variante "fast" depois da cheia. */
function modelRank(modelID: string): number {
  const id = modelID.toLowerCase();
  const gen = Number(/^gpt-(\d+(?:\.\d+)?)/.exec(id)?.[1] ?? 0);
  const fastPenalty = id.endsWith("-fast") ? 1 : 0;
  return gen * 10 - fastPenalty;
}

/**
 * Modelo mais avançado disponível na reserva (família Luna), entre os
 * declarados. Indisponíveis segundo a OpenAI são descartados; se todos
 * estiverem bloqueados, usa o modelo normal informado pela reserva.
 */
export function reserveTargetModel(
  usage: ProviderUsageInfo,
  declaredModelIDs: string[],
): string | null {
  const usable = (id: string) => usage.modelUsage?.[id]?.available !== false;
  const best = declaredModelIDs
    .filter((id) => isReserveModel(id) && usable(id))
    .sort((a, b) => modelRank(b) - modelRank(a))[0];
  if (best) return best;
  const slug = usage.reserve?.normalModelSlug;
  return slug && declaredModelIDs.includes(slug) ? slug : null;
}

/**
 * Troca automática ao entrar na reserva: retorna o modelo alvo quando a
 * reserva está ativa e o modelo atual é de fora da família da reserva ou está
 * marcado como indisponível. Se o modelo atual já é da reserva, a escolha do
 * usuário é respeitada (retorna null).
 */
export function needsReserveSwitch(
  usage: ProviderUsageInfo,
  currentModelID: string | null | undefined,
  declaredModelIDs: string[],
): string | null {
  if (!usage.reserve?.active || !currentModelID) return null;
  const currentUsable = usage.modelUsage?.[currentModelID]?.available !== false;
  if (isReserveModel(currentModelID) && currentUsable) return null;
  const target = reserveTargetModel(usage, declaredModelIDs);
  return target && target !== currentModelID ? target : null;
}

const CACHE_MS = 5 * 60 * 1000;
const cache = new Map<string, { at: number; data: ProviderUsageInfo }>();
const generations = new Map<string, number>();
const inFlight = new Map<string, { generation: number; promise: Promise<ProviderUsageInfo> }>();

export const PROVIDER_USAGE_UPDATED = "oc:provider-usage-updated";

/** Substitui/invalida o uso após um resgate e atualiza os medidores abertos. */
export function publishProviderUsage(providerID: string, data?: ProviderUsageInfo): void {
  generations.set(providerID, (generations.get(providerID) ?? 0) + 1);
  inFlight.delete(providerID);
  if (data) cache.set(providerID, { at: Date.now(), data });
  else cache.delete(providerID);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(PROVIDER_USAGE_UPDATED, { detail: { providerID } }));
  }
}

/**
 * Consulta o uso do provedor com cache de 5 minutos (para o medidor do chat
 * não bater na OpenAI a cada mensagem). `force` ignora o cache.
 */
export async function getProviderUsage(
  providerID: string,
  force = false,
): Promise<ProviderUsageInfo> {
  const hit = cache.get(providerID);
  if (hit && !force && Date.now() - hit.at < CACHE_MS) return hit.data;
  if (force) generations.set(providerID, (generations.get(providerID) ?? 0) + 1);
  const generation = generations.get(providerID) ?? 0;
  const pending = inFlight.get(providerID);
  if (pending?.generation === generation) return pending.promise;
  const promise = api.providerUsage(providerID)
    .then((data) => {
      // Uma consulta anterior ao resgate não pode restaurar os valores antigos.
      if (generation !== (generations.get(providerID) ?? 0)) return getProviderUsage(providerID);
      cache.set(providerID, { at: Date.now(), data });
      return data;
    })
    .catch((error: unknown) => {
      if (generation !== (generations.get(providerID) ?? 0)) return getProviderUsage(providerID);
      throw error;
    })
    .finally(() => {
      if (inFlight.get(providerID)?.promise === promise) inFlight.delete(providerID);
    });
  inFlight.set(providerID, { generation, promise });
  return promise;
}
